import { SubmissionError } from 'redux-form';
import { format } from 'date-fns';
import stripWhitespace from 'jl-design-system/utils/string/stripWhitespace';
import { resetForm } from 'jl-design-system/redux/actions/form/formActions';
import { APPLY_GIFT_VOUCHER } from '@constants/actionConstants';
import { URL_GIFT_VOUCHER } from '@constants/endpointConstants';
import { APPLY_GIFT_VOUCHER_FORM_ID } from '@utils/form/configs/giftOptions';
import errorCodeConstants from '@constants/errorCodeConstants';
import { GIFT_VOUCHER_SECURITY_CODE, GIFT_VOUCHER_SERIAL_NUMBER } from '@utils/form/configs/giftFields';
import handleInconsistentOrderFormState from '@utils/orderform/handleInconsistentOrderFormState';
import encrypt from '@utils/crypto/encrypt';
import determineSelectedPaymentType from './selectedPaymentTypeActions';

const {
  DUPLICATE_GIFT_VOUCHER,
  GIFT_VOUCHER_NOT_RECOGNISED,
  SECURITY_CODE_INVALID,
  SERIAL_NUMBER_INVALID,
} = errorCodeConstants;

const SECURITY_CODE = [GIFT_VOUCHER_SECURITY_CODE.id];
const SERIAL_NUMBER = [GIFT_VOUCHER_SERIAL_NUMBER.id];
const BOTH = [...SECURITY_CODE, ...SERIAL_NUMBER];
const errorDict = {
  [SECURITY_CODE_INVALID]: {
    causedBy: SECURITY_CODE,
    message: 'Please check you have entered the security code correctly and try again',
  },
  [SERIAL_NUMBER_INVALID]: {
    causedBy: SERIAL_NUMBER,
    message: 'Please check you have entered the serial number correctly and try again',
  },
  [DUPLICATE_GIFT_VOUCHER]: {
    causedBy: BOTH,
    message: 'You have already used this gift voucher',
  },
  [GIFT_VOUCHER_NOT_RECOGNISED]: {
    causedBy: BOTH,
    message: "We didn't recognise the voucher details you entered - please try again",
  },
};

export const applyGiftVoucher = ({
  serialNumber: serialNumberRaw,
  securityCode: securityCodeRaw,
  id = null,
  isUndo = false,
}) => async (dispatch, getState) => {
  const serialNumber = stripWhitespace(serialNumberRaw);
  const securityCode = stripWhitespace(securityCodeRaw);

  const giftingPaymentDetails = getState().payment?.giftingPaymentDetails || {};

  const {
    clientReference,
    publicKey,
    salt,
    token,
  } = giftingPaymentDetails;

  let encryptedDetails;
  try {
    encryptedDetails = await dispatch(encrypt({
      value: `${serialNumber}:${securityCode}:${clientReference}:${salt}:${format(new Date(), 'Hms')}`,
      publicKey,
    }));
  } catch (error) {
    return dispatch({
      type: `${APPLY_GIFT_VOUCHER}.FAILED`,
      error: {
        code: errorCodeConstants.GIFT_VOUCHER_SERVICE_UNAVAILABLE,
      },
      securityCode,
      serialNumber,
      isUndo,
    });
  }

  const body = { encryptedDetails, token };

  const response = await dispatch({
    type: APPLY_GIFT_VOUCHER,
    id,
    request: client => client({ path: URL_GIFT_VOUCHER, config: { body, method: 'POST' } }),
    serialNumber,
    securityCode,
    isUndo,
  });

  if (response.type === `${APPLY_GIFT_VOUCHER}.SUCCESS`) {
    dispatch(determineSelectedPaymentType());
    dispatch(resetForm(APPLY_GIFT_VOUCHER_FORM_ID));
  }

  if (response.type === `${APPLY_GIFT_VOUCHER}.FAILED`) {
    const code = response?.error?.code;
    const { causedBy, message } = errorDict[code] || {};

    if (
      code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE ||
      code === errorCodeConstants.INCOMPLETE_DELIVERY_DETAILS
    ) {
      await dispatch(handleInconsistentOrderFormState());
      return;
    }
    if (causedBy) {
      const errors = causedBy.reduce((acc, id) => ({
        ...acc,
        [id]: message,
      }), {});

      throw new SubmissionError(errors);
    }
  }

};

export default applyGiftVoucher;
